import { type ReactNode, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useAuthStore } from '@/shared/store/authStore'
import { useVisitRouteStore } from '@/features/visitplan/routeStore'
import { closeBottomSheet, closeModal } from '@/shared/ui'
import { listenFromHost, postToHost } from '@/shared/bridge/bridge'
import { useLayoutStore } from '@/shared/store/layoutStore'
import { hasOverlayHistoryEntry } from '@/shared/ui/useOverlayHistoryDismiss'
import { HostNavigationContext, hostBackAction } from '@/shared/shell/hostNavigation'
import { hasHostShell, isHostedWebShell } from '@/shared/bridge/env'

interface BridgeProviderProps {
  children: ReactNode
  /** 앱 뒤로가기가 여기서 멈추고 호스트로 넘어가는 역할별 홈 경로 */
  homePath: string
  /** 호스트 헤더 제목 — 역할별 메뉴에서 찾는다 */
  titleForPath: (pathname: string) => string | undefined
}

function isSafeInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

function closeOverlaysForHostNavigation() {
  closeModal({ skipHistoryBack: true })
  closeBottomSheet({ skipHistoryBack: true })
}

function closeVisitRouteFullscreen() {
  const { fullscreenOpen, setFullscreenOpen } = useVisitRouteStore.getState()
  if (!fullscreenOpen) return false

  setFullscreenOpen(false)
  return true
}

export function BridgeProvider({ children, homePath, titleForPath }: BridgeProviderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const setToken = useAuthStore((state) => state.setToken)
  const lastInboundPath = useRef<string | null>(null)
  const locationRef = useRef(location)
  const entryIndex = useRef<number>(window.history.state?.idx ?? 0)
  const goBack = () => {
    const layout = useLayoutStore.getState()
    const action = hostBackAction({
      overlayOpen: hasOverlayHistoryEntry(),
      drawerOpen: layout.drawerOpen,
      hasStackBack: !!layout.stackBack,
      isHome: location.pathname === homePath,
      currentIndex: window.history.state?.idx ?? 0,
      entryIndex: entryIndex.current,
    })
    if (action === 'overlay') window.history.back()
    else if (action === 'drawer') layout.setDrawerOpen(false)
    else if (action === 'stack') layout.stackBack?.()
    else if (action === 'history') navigate(-1)
    else postToHost({ type: 'CLOSE_WEBVIEW' })
  }
  const goBackRef = useRef(goBack)
  goBackRef.current = goBack

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    if (!hasHostShell()) return undefined
    postToHost({ type: 'READY', ...(isHostedWebShell() ? { payload: { navigationReady: true } } : {}) })

    return listenFromHost((msg) => {
      if (msg.type === 'SET_TOKEN') {
        setToken(msg.payload.accessToken, null)
        return
      }

      if (msg.type === 'GO_BACK') {
        if (isHostedWebShell()) {
          goBackRef.current()
          return
        }
        closeOverlaysForHostNavigation()
        if (closeVisitRouteFullscreen()) return

        const currentPath = `${locationRef.current.pathname}${locationRef.current.search}`
        if (currentPath !== homePath && window.history.length > 1) {
          navigate(-1)
          return
        }
        postToHost({ type: 'GO_BACK' })
        return
      }

      if (msg.type === 'NAVIGATE' && isSafeInternalPath(msg.payload.path)) {
        closeOverlaysForHostNavigation()
        closeVisitRouteFullscreen()
        lastInboundPath.current = msg.payload.path
        navigate(msg.payload.path)
      }
    })
  }, [homePath, navigate, setToken])

  useEffect(() => {
    if (!hasHostShell()) return
    const path = `${location.pathname}${location.search}`
    if (lastInboundPath.current === path) {
      lastInboundPath.current = null
      return
    }
    postToHost({ type: 'NAVIGATE', payload: { path, title: titleForPath(location.pathname) } })
  }, [location.pathname, location.search, titleForPath])

  return (
    <HostNavigationContext.Provider value={isHostedWebShell() ? goBack : null}>
      {children}
    </HostNavigationContext.Provider>
  )
}
